import { Link } from 'react-router-dom';
import { useState } from 'react';

const Footer = () => {
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitMessage, setSubmitMessage] = useState('');

  const handleNewsletterSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!email || !email.includes('@')) {
      setSubmitMessage('Please enter a valid email address');
      return;
    }

    setIsSubmitting(true);
    setSubmitMessage('');

    try {
      const formData = new URLSearchParams();
      formData.append('email', email);

      const response = await fetch('https://readdy.ai/api/form/d5p68ido3ikh5o9dul60', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formData.toString(),
      });

      if (response.ok) {
        setSubmitMessage('Thank you for subscribing!');
        setEmail('');
      } else {
        setSubmitMessage('Something went wrong. Please try again.');
      }
    } catch (error) {
      console.error('Newsletter submission error:', error);
      setSubmitMessage('Something went wrong. Please try again.');
    } finally {
      setIsSubmitting(false);
      setTimeout(() => setSubmitMessage(''), 5000);
    }
  };

  return (
    <footer className="bg-primary-charcoal text-white">
      <div className="max-w-7xl mx-auto px-6 lg:px-12 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
          {/* Column 1: Brand */}
          <div className="space-y-4">
            <div className="flex items-center space-x-3">
              <img
                src="https://static.readdy.ai/image/2bdd6ff17b3d38a4f0579550da7234da/bdf4bf99860934c329c77fa4595e712c.png"
                alt="ThriveEdge Logo"
                className="h-12 w-12 object-contain"
              />
              <span className="font-serif font-bold text-xl text-primary-gold">ThriveEdge</span>
            </div>
            <p className="text-gray-400 text-sm leading-relaxed">
              Empowering entrepreneurs with expert business consulting, e-commerce management, and holistic
              wellness coaching.
            </p>
            <div className="flex items-center space-x-4 pt-2">
              <a
                href="https://x.com/Oluwaseyilog"
                target="_blank"
                rel="noopener noreferrer"
                className="w-9 h-9 flex items-center justify-center rounded-full bg-white/10 hover:bg-primary-sage transition-all cursor-pointer"
                aria-label="X (Twitter)"
              >
                <i className="ri-twitter-x-line text-base"></i>
              </a>
              <a
                href="https://www.youtube.com/@TitilayoAjayi-q2w"
                target="_blank"
                rel="noopener noreferrer"
                className="w-9 h-9 flex items-center justify-center rounded-full bg-white/10 hover:bg-primary-sage transition-all cursor-pointer"
                aria-label="YouTube"
              >
                <i className="ri-youtube-line text-base"></i>
              </a>
              <a
                href="https://www.instagram.com/aanuo_luwa"
                target="_blank"
                rel="noopener noreferrer"
                className="w-9 h-9 flex items-center justify-center rounded-full bg-white/10 hover:bg-primary-sage transition-all cursor-pointer"
                aria-label="Instagram"
              >
                <i className="ri-instagram-line text-base"></i>
              </a>
              <a
                href="https://www.facebook.com/TitilayoAjayi"
                target="_blank"
                rel="noopener noreferrer"
                className="w-9 h-9 flex items-center justify-center rounded-full bg-white/10 hover:bg-primary-sage transition-all cursor-pointer"
                aria-label="Facebook"
              >
                <i className="ri-facebook-fill text-base"></i>
              </a>
            </div>
          </div>

          {/* Column 2: Quick Links */}
          <div>
            <h4 className="font-serif font-semibold text-lg text-primary-gold mb-4">Quick Links</h4>
            <ul className="space-y-3">
              <li>
                <Link
                  to="/"
                  className="text-gray-300 hover:text-primary-gold transition-colors text-sm cursor-pointer whitespace-nowrap"
                >
                  Home
                </Link>
              </li>
              <li>
                <Link
                  to="/about"
                  className="text-gray-300 hover:text-primary-gold transition-colors text-sm cursor-pointer whitespace-nowrap"
                >
                  About
                </Link>
              </li>
              <li>
                <Link
                  to="/services"
                  className="text-gray-300 hover:text-primary-gold transition-colors text-sm cursor-pointer whitespace-nowrap"
                >
                  Services
                </Link>
              </li>
              <li>
                <Link
                  to="/success-stories"
                  className="text-gray-300 hover:text-primary-gold transition-colors text-sm cursor-pointer whitespace-nowrap"
                >
                  Success Stories
                </Link>
              </li>
              <li>
                <Link
                  to="/blog"
                  className="text-gray-300 hover:text-primary-gold transition-colors text-sm cursor-pointer whitespace-nowrap"
                >
                  Blog
                </Link>
              </li>
              <li>
                <Link
                  to="/contact"
                  className="text-gray-300 hover:text-primary-gold transition-colors text-sm cursor-pointer whitespace-nowrap"
                >
                  Contact
                </Link>
              </li>
            </ul>
          </div>

          {/* Column 3: Get In Touch */}
          <div>
            <h4 className="font-serif font-semibold text-lg text-primary-gold mb-4">Get In Touch</h4>
            <ul className="space-y-3">
              <li className="flex items-start space-x-3">
                <i className="ri-mail-line text-primary-gold mt-1"></i>
                <a
                  href="mailto:titilayoajayi923@gmail.com"
                  className="text-gray-300 hover:text-primary-gold transition-colors text-sm cursor-pointer break-all"
                >
                  titilayoajayi923@gmail.com
                </a>
              </li>
              <li className="flex items-start space-x-3">
                <i className="ri-phone-line text-primary-gold mt-1"></i>
                <div className="flex flex-col space-y-1">
                  <a
                    href="tel:+2349060814354"
                    className="text-gray-300 hover:text-primary-gold transition-colors text-sm cursor-pointer whitespace-nowrap"
                  >
                    +234 906 081 4354
                  </a>
                  <a
                    href="tel:+2349047390448"
                    className="text-gray-300 hover:text-primary-gold transition-colors text-sm cursor-pointer whitespace-nowrap"
                  >
                    +234 904 739 0448
                  </a>
                </div>
              </li>
              <li className="flex items-start space-x-3">
                <i className="ri-whatsapp-line text-primary-gold mt-1"></i>
                <a
                  href="https://wa.me/2349047390448"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-gray-300 hover:text-primary-gold transition-colors text-sm cursor-pointer whitespace-nowrap"
                >
                  +234 904 739 0448
                </a>
              </li>
            </ul>
          </div>

          {/* Column 4: Newsletter */}
          <div>
            <h4 className="font-serif font-semibold text-lg text-primary-gold mb-4">Stay Inspired</h4>
            <p className="text-gray-400 text-sm mb-4">Subscribe for business tips and wellness insights</p>
            <form
              onSubmit={handleNewsletterSubmit}
              id="newsletter-footer"
              data-readdy-form
              className="space-y-3"
            >
              <div className="flex">
                <input
                  type="email"
                  name="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Your email"
                  className="flex-1 px-4 py-2 bg-white/10 border border-white/20 rounded-l-lg text-white placeholder-gray-400 text-sm focus:outline-none focus:border-primary-gold transition-colors"
                  required
                  disabled={isSubmitting}
                />
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="px-4 py-2 bg-primary-gold hover:bg-accent-warm-gold text-white rounded-r-lg transition-colors cursor-pointer disabled:opacity-50 whitespace-nowrap"
                  aria-label="Subscribe"
                >
                  <i className="ri-arrow-right-line"></i>
                </button>
              </div>
              {submitMessage && (
                <p
                  className={`text-xs ${
                    submitMessage.includes('Thank you') ? 'text-green-400' : 'text-red-400'
                  }`}
                >
                  {submitMessage}
                </p>
              )}
            </form>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="mt-12 pt-8 border-t border-white/10">
          <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
            <p className="text-gray-400 text-sm text-center md:text-left">
              © 2025 ThriveEdge by Titilayo Ajayi. Empowering Growth &amp; Wellness.
            </p>
            <a
              href="https://readdy.ai/?ref=logo"
              target="_blank"
              rel="noopener noreferrer"
              className="text-gray-400 hover:text-primary-gold text-sm transition-colors cursor-pointer whitespace-nowrap"
            >
              Powered by Readdy
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
